/**
 * This wrapper generates error messages from v8n Validation Errors. https://imbrn.github.io/v8n/api/#validationerror
 * They are logged using LogManager as a warning to our customers when they mess up arguments.
 * To use: `import { enums, validation as v8n, parse } from '<relativePath>/common/validation'`.
 * Build up your validation using v8n.string, v8n.number, v8n.schema, enums, etc.
 * Enums is not part of the v8n library, but provided here for ease of use
 * const v8nValidation = v8n.schema({
      sdpSemantics: enums(['unified-plan', 'plan-b']),
      iceServers: v8n.array(),
      iceCollectionIdealTimeout: v8n.positive(),
      serverTurnCredentials: v8n.boolean(),
      iceCollectionCheckFunction: v8n.optional(v8n.function())
    })
 * Create a validator like this: `const parseOptions = parse('nameOfArg', v8nValidation)`
 * Run that validator against the actual values: `parseOptions(options)`
 * Returns the input either way and logs errors, in the future will throw errors on invalid data.
 */
import { __, concat, flatMap, isArray, isEmpty, join, map, partial } from 'lodash/fp'
import { logManager } from '../../logs'
import v8n from 'v8n'

// Custom v8n Rules - https://imbrn.github.io/v8n/api/#extend
v8n.extend({
  function: () => value => typeof value === 'function'
})

// EXPORTS / IO
export const validation = v8n()

// TODO: Add an enums function to the v8n library
// Use as such: `prop: enums( [ 'red', 'blue', 'green' ] )
export const enums = values => {
  // Map iteratee isn't just `v8n().exact` due to v8n() returning a new ProxyContext each time
  const v8nExact = value => v8n().exact(value)
  const exactValues = map(v8nExact)(values)
  return v8n().passesAnyOf(...exactValues)
}

// Name -> v8nRule -> Input -> [String]
export const errorMessages = name => v8nRule => input =>
  flatMap(validationErrorMessages(name))(validationResults(v8nRule)(input))

export const validationResults = v8nRule => input => v8nRule.testAll(input)

// (Name, v8nRule) -> Input -> IO Input
export const parse = (name, v8nRule) => input => {
  const errors = errorMessages(name)(v8nRule)(input)

  if (isEmpty(errors)) {
    return input
  } else {
    const log = logManager.getLogger('VALIDATION')
    log.info(prettyPrint(errors)) // This is an IO side-effect
    return input
  }
}

// DEFINITIONS

// Name -> ValidationError -> [String]
// https://imbrn.github.io/v8n/api/#validationerror
const validationErrorMessages = name => validationError => {
  const startingPath = []
  return validationErrorMessageHelper(startingPath)(name)(validationError)
}

// [String] -> Name -> ValidationError -> [String]
const validationErrorMessageHelper = acc => name => ({ cause, rule, target, value }) => {
  const path = concat(acc)(target || name)
  if (isArray(cause)) {
    const newVEM = validationErrorMessageHelper(path)(target)
    return flatMap(newVEM)(cause)
  } else {
    return `${join('.')(path)} has value of '${value}', but it should be ${ruleMessage(rule)}`
  }
}

// Rule -> String
// https://imbrn.github.io/v8n/api/#rule
const ruleMessage = ({ name, args }) => {
  switch (name) {
    case 'schema':
      return 'Schema'

    case 'passesAnyOf':
      return `any of '${join("', '")(map(enumName)(args))}'`

    case 'between':
      return `between ${args.join(', ')}`

    default:
      return `${aOrAn(name)} ${name}`
  }
}

// HELPERS
const enumName = arg => arg.chain[0].args // v8n internals
const prettyPrint = partial(JSON.stringify)([__, null, 4])
// https://dictionary.cambridge.org/grammar/british-grammar/a-an-and-the
const aOrAn = string => (/[aeiou]/i.test(string[0]) ? 'an' : 'a')
